import { useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';

/**
 * Magic-link auth gate. No passwords (per spec). Children render only once a
 * session exists; otherwise we show an email form that sends a one-time link.
 * The session is detected from the URL on return (detectSessionInUrl in the client).
 */
export function AuthGate({ children }: { children: (session: Session) => ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setReady(true);
      return;
    }
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  async function sendLink(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: window.location.origin },
    });
    setBusy(false);
    if (error) setError(error.message);
    else setSent(true);
  }

  if (!ready) return <p className="muted" style={{ padding: 16 }}>Loading…</p>;

  if (!isSupabaseConfigured) {
    return (
      <div className="card">
        <h2>Sign in</h2>
        <p className="error">Supabase isn’t configured, so sign-in is unavailable.</p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="auth-wrap">
      <div className="card">
        <h2>Sign in</h2>
        {sent ? (
          <p>
            Magic link sent to <strong>{email}</strong>. Open it on this device to continue.
          </p>
        ) : (
          <form onSubmit={sendLink} className="field">
            <span>Email (magic link — no password)</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />
            <div className="row" style={{ marginTop: 8 }}>
              <button className="btn btn-primary" disabled={busy || !email}>
                {busy ? 'Sending…' : 'Send magic link'}
              </button>
            </div>
            {error && <p className="error">{error}</p>}
          </form>
        )}
      </div>
      </div>
    );
  }

  return <>{children(session)}</>;
}

export function SignOutButton() {
  return (
    <button className="btn" onClick={() => void supabase.auth.signOut()}>
      Sign out
    </button>
  );
}
